import React, { useEffect, useMemo, useState } from 'react';
import { X, Repeat, CalendarClock } from 'lucide-react';
import useMobileModalMode from '../hooks/useMobileModalMode';
import {
  COMPANY_TASK_CURRENCIES,
  mergeCategoryOptions,
  formatTaskMonth,
} from '../utils/companyTaskUtils';

const RECURRENCE_OPTIONS = [
  { value: 'once', label: 'Tek seferlik' },
  { value: 'monthly', label: 'Aylık' },
  { value: 'quarterly', label: '3 Aylık' },
  { value: 'yearly', label: 'Yıllık' },
  { value: 'year_end', label: 'Yıl sonuna kadar' },
];

const dayFromDueDate = (dueDate) => {
  if (!dueDate) return 1;
  const day = Number(dueDate.split('-')[2]);
  return Number.isFinite(day) ? day : 1;
};

// Sablonun period/endMonth degerinden UI tekrar secenegine cevir (recurrenceToBackend'in tersi).
const backendToRecurrence = (template, monthKey) => {
  if (!template) return 'once';
  const year = (monthKey || '').slice(0, 4);
  if (template.period === 'quarterly') return 'quarterly';
  if (template.period === 'yearly') return 'yearly';
  if (template.period === 'monthly' && template.endMonth === `${year}-12`) return 'year_end';
  return 'monthly';
};

const getInitialState = (initialData, monthKey, template) => ({
  title: initialData?.title || '',
  category: initialData?.category || 'Vergi',
  description: initialData?.description || '',
  dueDay: initialData ? dayFromDueDate(initialData.dueDate) : 1,
  plannedAmount: initialData?.plannedAmount ?? '',
  currency: initialData?.currency || 'TRY',
  assignedUserIds: initialData?.assignedUserIds || (initialData?.assignedUserId ? [initialData.assignedUserId] : []),
  requiresProof: Boolean(initialData?.requiresProof),
  recurrence: initialData?.templateId ? backendToRecurrence(template, monthKey) : 'once',
  scope: 'future',
});

const CompanyTaskModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  monthKey,
  isSubmitting,
  categoryOptions = [],
  users = [],
  template = null,
}) => {
  const [formData, setFormData] = useState(getInitialState(initialData, monthKey, template));

  useMobileModalMode(isOpen);

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialState(initialData, monthKey, template));
    }
  }, [initialData, isOpen, monthKey, template]);

  const categories = useMemo(() => mergeCategoryOptions(categoryOptions), [categoryOptions]);

  if (!isOpen) {
    return null;
  }

  const isEdit = Boolean(initialData);
  const isRecurringEdit = isEdit && !initialData.isManual && initialData.templateId;
  const isManualEdit = isEdit && (initialData.isManual || !initialData.templateId);

  const set = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const toggleAssignedUser = (userId) => {
    setFormData((prev) => {
      const normalizedUserId = Number(userId);
      const assignedUserIds = prev.assignedUserIds.includes(normalizedUserId)
        ? prev.assignedUserIds.filter((id) => id !== normalizedUserId)
        : [...prev.assignedUserIds, normalizedUserId];
      return { ...prev, assignedUserIds };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const fields = {
      title: formData.title.trim(),
      category: (formData.category || 'Diger').trim(),
      description: formData.description.trim() || null,
      dueDay: Math.min(31, Math.max(1, Number(formData.dueDay) || 1)),
      plannedAmount: formData.plannedAmount === '' ? 0 : Number(formData.plannedAmount),
      currency: formData.currency,
      assignedUserIds: formData.assignedUserIds,
      requiresProof: formData.requiresProof,
    };

    const originalRecurrence = getInitialState(initialData, monthKey, template).recurrence;
    const recurrenceChanged = isEdit && formData.recurrence !== originalRecurrence;

    let mode;
    if (!isEdit) {
      mode = formData.recurrence === 'once' ? 'create-oneoff' : 'create-recurring';
    } else if (isManualEdit) {
      // Manuel gorev: 'Tek seferlik' kalirsa item guncelle, aksi halde tekrarliya cevir.
      mode = formData.recurrence === 'once' ? 'update-item' : 'convert-recurring';
    } else {
      // Tekrarli gorev: periyot degisikligi veya 'Bu ay ve sonrasi' -> seri; degilse tek ay.
      mode = (formData.scope === 'future' || recurrenceChanged) ? 'update-series' : 'update-item';
    }

    onSubmit({
      mode,
      fields,
      recurrence: formData.recurrence,
      scope: formData.scope,
      templateId: isRecurringEdit ? initialData.templateId : null,
      itemId: isEdit ? initialData.id : null,
    });
  };

  const inputClass =
    'w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-white focus:border-primary-500 focus:outline-none';

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/80 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[calc(100dvh-0.5rem)] w-full max-w-2xl flex-col rounded-t-2xl border border-slate-700 bg-slate-900 text-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-700/70 p-5 sm:p-6">
          <div>
            <h2 className="text-xl font-bold">{isEdit ? 'Görevi düzenle' : 'Yeni görev'}</h2>
            <p className="mt-1 text-sm text-slate-400">{formatTaskMonth(monthKey)} dönemi</p>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="space-y-5 overflow-y-auto p-5 sm:p-6">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Görev adı</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => set({ title: e.target.value })}
                className={inputClass}
                placeholder="Örn: KDV beyannamesi"
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Kategori</label>
                <input
                  type="text"
                  list="company-task-categories"
                  value={formData.category}
                  onChange={(e) => set({ category: e.target.value })}
                  className={inputClass}
                  placeholder="Vergi, SGK, Kira..."
                  required
                />
                <datalist id="company-task-categories">
                  {categories.map((category) => <option key={category} value={category} />)}
                </datalist>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Ayın günü (son gün)</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.dueDay}
                  onChange={(e) => set({ dueDay: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Planlanan tutar</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.plannedAmount}
                  onChange={(e) => set({ plannedAmount: e.target.value })}
                  className={inputClass}
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Para birimi</label>
                <select
                  value={formData.currency}
                  onChange={(e) => set({ currency: e.target.value })}
                  className={inputClass}
                >
                  {COMPANY_TASK_CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-300">Sorumlular</label>
                <div className="grid gap-2 rounded-xl border border-slate-700 bg-slate-950/70 p-3 sm:grid-cols-2">
                  {users.map((u) => {
                    const userId = Number(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-3 rounded-lg px-2 py-2 text-sm text-slate-200 hover:bg-slate-800/70">
                        <input
                          type="checkbox"
                          checked={formData.assignedUserIds.includes(userId)}
                          onChange={() => toggleAssignedUser(userId)}
                          className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-primary-500 focus:ring-primary-500"
                        />
                        <span className="min-w-0 truncate">{u.username} ({u.role})</span>
                      </label>
                    );
                  })}
                  {users.length === 0 && (
                    <div className="px-2 py-2 text-sm text-slate-500">Kullanıcı bulunamadı</div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-300">Açıklama (opsiyonel)</label>
                <textarea
                  rows="2"
                  value={formData.description}
                  onChange={(e) => set({ description: e.target.value })}
                  className={inputClass}
                  placeholder="Banka, fatura tipi, not..."
                />
              </div>
            </div>

            <label className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={formData.requiresProof}
                onChange={(e) => set({ requiresProof: e.target.checked })}
                className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-primary-500 focus:ring-primary-500"
              />
              Dekont / belge zorunlu (tamamlamak için yükleme istenir)
            </label>

            {(
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <Repeat size={16} className="text-primary-400" /> Tekrar
                  {isRecurringEdit && <span className="text-xs font-normal text-slate-500">(değişiklik seriye uygulanır)</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {RECURRENCE_OPTIONS.map((opt) => {
                    const active = formData.recurrence === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => set({ recurrence: opt.value })}
                        className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${active
                          ? 'border-primary-500 bg-primary-500/15 text-primary-300'
                          : 'border-slate-700 text-slate-300 hover:border-slate-600'}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {formData.recurrence !== 'once' && (
                  <p className="mt-3 text-xs text-slate-400">
                    {formData.recurrence === 'year_end'
                      ? `Bu aydan ${monthKey?.slice(0, 4)} Aralık ayına kadar her ay otomatik eklenir.`
                      : formData.recurrence === 'quarterly'
                        ? `${formatTaskMonth(monthKey)} başlangıç alınır; her 3 ayda bir otomatik eklenir.`
                        : formData.recurrence === 'yearly'
                          ? `${formatTaskMonth(monthKey)} başlangıç alınır; her yıl aynı ay otomatik eklenir.`
                          : 'Her ay otomatik eklenir, durdurana kadar devam eder.'}{' '}
                    Her ayın tutarı ayrı düzenlenebilir.
                  </p>
                )}
              </div>
            )}

            {isRecurringEdit && (
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <CalendarClock size={16} className="text-primary-400" /> Bu değişiklik nereye uygulansın?
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => set({ scope: 'this' })}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${formData.scope === 'this'
                      ? 'border-primary-500 bg-primary-500/15 text-primary-300'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'}`}
                  >
                    Sadece bu ay
                  </button>
                  <button
                    type="button"
                    onClick={() => set({ scope: 'future' })}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${formData.scope === 'future'
                      ? 'border-primary-500 bg-primary-500/15 text-primary-300'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'}`}
                  >
                    Bu ay ve sonrası
                  </button>
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  "Bu ay ve sonrası" gelecekteki tamamlanmamış tekrarları da günceller; geçmiş aylar korunur.
                </p>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 flex justify-end gap-3 border-t border-slate-700/70 bg-slate-900/95 p-4 pb-safe-offset sm:p-6">
            <button type="button" onClick={onClose} className="rounded-xl px-5 py-2.5 font-medium text-slate-300 transition-colors hover:bg-slate-800">
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-primary-600 px-6 py-2.5 font-bold text-white transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              {isSubmitting ? 'Kaydediliyor...' : (isEdit ? 'Kaydet' : 'Görevi ekle')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyTaskModal;
